/**
 * The service reports (`/service/reports`; LIAA service plan S10 — P1-17; designs ReportWorkload, ReportRevenue,
 * ReportStates): workload and efficiency of the performers or the workplaces, revenue and margin by work type with the
 * labour revenue by performer. The screen's table is the export's — one calculation on the server; "Export XLSX / PDF"
 * starts a job, the Exports list shows it preparing, ready to download, or failed with "Retry". For `service.reports`.
 */

import { useInfiniteQuery, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Button, Card, DatePicker, Empty, Flex, Popover, Segmented, Select, Skeleton, Table, Tabs, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import * as api from './serviceReportsApi';
import { workOrderKeys } from '../api/queryKeys';
import { formatMoney } from '../utils/format';
import { useWorkOrderError } from '../utils/useWorkOrderError';

import type { Cell, ReportJob, ReportResult, ServiceReportKey } from './serviceReportsApi';

import { useWorkTypes } from '@/features/service-setup';

const { RangePicker } = DatePicker;
const WARN_BG = '#fffbe6';

/** "78 h 20" — minutes as the design writes a duration. */
export function formatHours(minutes: Cell | undefined): string {
  if (typeof minutes !== 'number') return '—';
  const m = Math.round(minutes);
  if (m < 60) return `${m} min`;
  return m % 60 === 0 ? `${Math.floor(m / 60)} h` : `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')}`;
}

/** A ratio as a percent, `digits` after the point; null — no data. */
function percent(ratio: Cell | undefined, digits: number, noData: string): string {
  return typeof ratio === 'number' ? `${(ratio * 100).toFixed(digits)} %` : noData;
}

const num = (v: Cell | undefined): number => (typeof v === 'number' ? v : 0);

export function ServiceReportsPage(): React.JSX.Element {
  const { t, i18n } = useTranslation('workOrders');
  const { message } = App.useApp();
  const describe = useWorkOrderError();
  const queryClient = useQueryClient();
  const lang: 'lv' | 'en' = i18n.language.startsWith('en') ? 'en' : 'lv';
  const [key, setKey] = useState<ServiceReportKey>('service-workload');
  const [mode, setMode] = useState<'staff' | 'resources'>('staff');
  const [workTypeId, setWorkTypeId] = useState<string | null>(null);
  const [range, setRange] = useState<[string, string]>([dayjs().startOf('month').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')]);
  const workTypes = useWorkTypes();
  const typeName = (id: string | undefined | null): string => {
    const found = workTypes.data?.find((w) => w.id === id);
    return found === undefined ? t('reports.allTypes') : lang === 'en' ? found.nameEn : found.nameLv;
  };

  const filters = {
    ...(key === 'service-workload' ? { mode } : {}),
    ...(workTypeId !== null ? { workTypeId } : {}),
    language: lang,
  };
  const request = { key, period: { from: range[0], to: range[1] }, filters };
  const report = useQuery({
    queryKey: [...workOrderKeys.all, 'reports', 'preview', request],
    queryFn: () => api.previewReport(request),
    meta: { suppressGlobalError: true },
  });

  const preparing = (j: ReportJob): boolean => j.status === 'queued' || j.status === 'running';
  // The whole history, a page at a time ("Show more" — no ceiling); the loaded pages ask again while one prepares.
  const jobs = useInfiniteQuery({
    queryKey: [...workOrderKeys.all, 'reports', 'jobs'],
    queryFn: ({ pageParam }) => api.fetchReportJobs(pageParam),
    initialPageParam: 1,
    getNextPageParam: (last, pages) => (pages.length * api.JOBS_PAGE < last.total ? pages.length + 1 : undefined),
    refetchInterval: (q) => ((q.state.data?.pages ?? []).some((p) => p.jobs.some(preparing)) ? 2000 : false),
    meta: { suppressGlobalError: true },
  });
  // The exports started here are followed by their own route until they settle — wherever newer ones push them.
  const [mine, setMine] = useState<string[]>([]);
  const followed = useQueries({
    queries: mine.map((id) => ({
      queryKey: [...workOrderKeys.all, 'reports', 'job', id],
      queryFn: () => api.fetchReportJob(id),
      refetchInterval: (q: { state: { data?: ReportJob } }) => (q.state.data === undefined || preparing(q.state.data) ? 2000 : false),
      meta: { suppressGlobalError: true },
    })),
  });
  const [exportsOpen, setExportsOpen] = useState(false);
  // A ready link runs out (15 minutes): while the list is open the page wakes at the next expiry and re-reads the clock.
  const [now, setNow] = useState(() => Date.now());
  const loaded = [...(jobs.data?.pages ?? []).flatMap((p) => p.jobs)];
  const expiries = [...loaded, ...followed.flatMap((f) => (f.data !== undefined ? [f.data] : []))]
    .filter((j) => j.status === 'completed' && j.downloadExpiresAt !== null)
    .map((j) => Date.parse(j.downloadExpiresAt as string))
    .filter((at) => at > now);
  const nextExpiry = expiries.length === 0 ? null : Math.min(...expiries);
  useEffect(() => {
    if (!exportsOpen) return undefined;
    setNow(Date.now());
    if (nextExpiry === null) return undefined;
    const timer = setTimeout(() => setNow(Date.now()), Math.max(0, nextExpiry - Date.now()) + 50);
    return () => clearTimeout(timer);
  }, [exportsOpen, nextExpiry]);
  const exportReport = useMutation({
    mutationFn: (body: api.ReportRequest) => api.generateReport(body),
    onSuccess: (started) => {
      setExportsOpen(true);
      setMine((ids) => [started.id, ...ids.filter((x) => x !== started.id)]);
      void queryClient.invalidateQueries({ queryKey: [...workOrderKeys.all, 'reports', 'jobs'] });
    },
    onError: (e) => void message.error(describe(e)),
    meta: { suppressGlobalError: true },
  });

  const periodText = (p: { from: string; to: string }): string => `${dayjs(p.from).format('DD.MM')}–${dayjs(p.to).format('DD.MM.YYYY')}`;
  const jobLabel = (j: ReportJob): string =>
    [
      j.key === 'service-workload' ? t('reports.tab.workloadShort') : t('reports.tab.revenueShort'),
      ...(j.key === 'service-workload' ? [t(`reports.mode.${j.filters.mode ?? 'staff'}`), typeName(j.filters.workTypeId)] : []),
      periodText(j.period),
      j.format.toUpperCase(),
    ].join(' · ');

  const again = (j: ReportJob): void => exportReport.mutate({ key: j.key as ServiceReportKey, format: j.format, period: j.period, filters: j.filters });
  // One row per export, in its newest known state — the history's or its own route's, whichever is later; a settled state
  // (ready, failed) is never taken back by an older "preparing". An export started here that the loaded pages do not
  // show (pushed out by newer ones) goes on top.
  const settled = (j: ReportJob): boolean => j.status === 'completed' || j.status === 'failed';
  const newer = (a: ReportJob, b: ReportJob): ReportJob =>
    settled(a) !== settled(b) ? (settled(a) ? a : b) : Date.parse(a.updatedAt) >= Date.parse(b.updatedAt) ? a : b;
  const tracked = new Map(followed.flatMap((f) => (f.data !== undefined ? [[f.data.id, f.data] as const] : [])));
  const own = [...tracked.values()].filter((j) => !loaded.some((l) => l.id === j.id));
  const list = [...own, ...loaded.map((j) => (tracked.has(j.id) ? newer(tracked.get(j.id) as ReportJob, j) : j))];
  const exportsList = (
    <Flex vertical gap={8} role="region" aria-label={t('reports.exports')} style={{ minWidth: 320 }}>
      {jobs.isPending ? <Skeleton active paragraph={{ rows: 2 }} title={false} /> : null}
      {jobs.isError ? (
        <Alert
          type="error"
          showIcon
          message={t('reports.exportsFailed')}
          action={
            <Button size="small" onClick={() => void jobs.refetch()}>
              {t('reports.retry')}
            </Button>
          }
        />
      ) : null}
      {jobs.isSuccess && list.length === 0 ? <Typography.Text type="secondary">{t('reports.noExports')}</Typography.Text> : null}
      {list.map((j) => (
        <Flex key={j.id} justify="space-between" align="center" gap={12}>
          <Flex vertical>
            <Typography.Text>{jobLabel(j)}</Typography.Text>
            <Typography.Text type={j.status === 'failed' ? 'danger' : 'secondary'} style={{ fontSize: 12 }}>
              {api.expired(j, now)
                ? t('reports.expired')
                : j.status === 'completed'
                  ? t('reports.ready', { at: dayjs(j.updatedAt).format('HH:mm') })
                  : j.status === 'failed'
                    ? t('reports.failed')
                    : t('reports.preparing')}
            </Typography.Text>
          </Flex>
          {api.expired(j, now) ? (
            <Button size="small" onClick={() => again(j)}>
              {t('reports.again')}
            </Button>
          ) : j.status === 'completed' ? (
            <Button type="primary" size="small" href={api.downloadHref(j)}>
              {t('reports.download')}
            </Button>
          ) : j.status === 'failed' ? (
            <Button size="small" onClick={() => again(j)}>
              {t('reports.retry')}
            </Button>
          ) : null}
        </Flex>
      ))}
      {jobs.hasNextPage ? (
        <Button type="link" size="small" loading={jobs.isFetchingNextPage} onClick={() => void jobs.fetchNextPage()}>
          {t('reports.more')}
        </Button>
      ) : null}
    </Flex>
  );

  return (
    <Flex vertical gap={16}>
      <Flex justify="space-between" align="center" wrap gap={8}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          {t('reports.title')}
        </Typography.Title>
        <Flex gap={8} wrap>
          {(['xlsx', 'pdf'] as const).map((format) => (
            <Button key={format} loading={exportReport.isPending && exportReport.variables?.format === format} onClick={() => exportReport.mutate({ ...request, format })}>
              {t(`reports.export.${format}`)}
            </Button>
          ))}
          <Popover trigger="click" open={exportsOpen} onOpenChange={setExportsOpen} content={exportsList} placement="bottomRight">
            <Button>{t('reports.exports')}</Button>
          </Popover>
        </Flex>
      </Flex>
      <Tabs
        activeKey={key}
        onChange={(k) => setKey(k as ServiceReportKey)}
        items={[
          { key: 'service-workload', label: t('reports.tab.workload') },
          { key: 'service-revenue', label: t('reports.tab.revenue') },
        ]}
      />
      <Flex gap={12} wrap align="center">
        <Typography.Text>{t('reports.period')}</Typography.Text>
        <RangePicker
          placeholder={[t('reports.periodStart'), t('reports.periodEnd')]}
          format="DD.MM.YYYY"
          allowClear={false}
          value={[dayjs(range[0]), dayjs(range[1])]}
          onChange={(v) => {
            if (v?.[0] && v[1]) setRange([v[0].format('YYYY-MM-DD'), v[1].format('YYYY-MM-DD')]);
          }}
        />
        {key === 'service-workload' ? (
          <Segmented
            aria-label={t('reports.show')}
            value={mode}
            onChange={(v) => setMode(v as 'staff' | 'resources')}
            options={[
              { value: 'staff', label: t('reports.mode.staff') },
              { value: 'resources', label: t('reports.mode.resources') },
            ]}
          />
        ) : (
          <Typography.Text type="secondary">{t('reports.revenueNote')}</Typography.Text>
        )}
        <Select
          aria-label={t('reports.workType')}
          style={{ minWidth: 200 }}
          value={workTypeId ?? ''}
          onChange={(v: string) => setWorkTypeId(v === '' ? null : v)}
          options={[{ value: '', label: t('reports.allTypes') }, ...(workTypes.data ?? []).filter((w) => !w.archived).map((w) => ({ value: w.id, label: lang === 'en' ? w.nameEn : w.nameLv }))]}
        />
      </Flex>
      {report.isPending ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : report.isError ? (
        <Alert type="error" showIcon message={describe(report.error)} />
      ) : key === 'service-workload' ? (
        <Workload result={report.data} mode={mode} />
      ) : (
        <Revenue result={report.data} />
      )}
    </Flex>
  );
}

function Kpi({ label, value, hint }: { label: string; value: string; hint: string }): React.JSX.Element {
  return (
    <Card size="small" style={{ flex: '1 1 180px' }}>
      <Typography.Text type="secondary">{label}</Typography.Text>
      <div style={{ fontSize: 22, fontWeight: 600 }}>{value}</div>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {hint}
      </Typography.Text>
    </Card>
  );
}

function Workload({ result, mode }: { result: ReportResult; mode: 'staff' | 'resources' }): React.JSX.Element {
  const { t } = useTranslation('workOrders');
  const noData = t('reports.noData');
  const s = result.summary;
  const staff = mode === 'staff';
  const rows = [...result.rows.map((r, i) => ({ ...r, _key: String(i) })), { ...s, name: t('reports.total'), _key: 'total' }];
  const columns = [
    { title: staff ? t('reports.col.performer') : t('reports.col.workplace'), dataIndex: 'name', key: 'name' },
    { title: t('reports.col.capacity'), key: 'cap', align: 'right' as const, render: (_: unknown, r: Record<string, Cell>) => formatHours(r.capacityMinutes) },
    { title: t('reports.col.booked'), key: 'booked', align: 'right' as const, render: (_: unknown, r: Record<string, Cell>) => formatHours(r.bookedMinutes) },
    { title: staff ? t('reports.col.actual') : t('reports.col.done'), key: 'actual', align: 'right' as const, render: (_: unknown, r: Record<string, Cell>) => formatHours(r.actualMinutes) },
    { title: t('reports.col.load'), key: 'load', align: 'right' as const, render: (_: unknown, r: Record<string, Cell>) => percent(r.load, 0, noData) },
    ...(staff
      ? [
          { title: t('reports.col.normDone'), key: 'norm', align: 'right' as const, render: (_: unknown, r: Record<string, Cell>) => formatHours(r.normDoneMinutes) },
          { title: t('reports.col.actualDone'), key: 'actualDone', align: 'right' as const, render: (_: unknown, r: Record<string, Cell>) => formatHours(r.actualDoneMinutes) },
          { title: t('reports.col.efficiency'), key: 'eff', align: 'right' as const, render: (_: unknown, r: Record<string, Cell>) => percent(r.efficiency, 0, noData) },
        ]
      : []),
  ];
  return (
    <Flex vertical gap={16}>
      <Flex gap={12} wrap>
        <Kpi label={t('reports.kpi.capacity')} value={formatHours(s.capacityMinutes)} hint={staff ? t('reports.kpi.capacityStaff') : t('reports.kpi.capacityResources')} />
        {staff ? (
          <>
            <Kpi label={t('reports.kpi.actualLoad')} value={`${formatHours(s.actualMinutes)} · ${percent(s.load, 0, noData)}`} hint={t('reports.kpi.actualLoadHint')} />
            <Kpi label={t('reports.kpi.normDone')} value={formatHours(s.normDoneMinutes)} hint={t('reports.kpi.normDoneHint')} />
            <Kpi label={t('reports.kpi.efficiency')} value={percent(s.efficiency, 0, noData)} hint={t('reports.kpi.efficiencyHint')} />
          </>
        ) : (
          <>
            <Kpi label={t('reports.kpi.booked')} value={formatHours(s.bookedMinutes)} hint={t('reports.kpi.bookedHint')} />
            <Kpi label={t('reports.kpi.doneLoad')} value={`${formatHours(s.actualMinutes)} · ${percent(s.load, 0, noData)}`} hint={t('reports.kpi.doneLoadHint')} />
          </>
        )}
      </Flex>
      <Card size="small">
        {result.rows.length === 0 ? (
          <Empty description={t('reports.empty')} />
        ) : (
          <Table size="small" rowKey="_key" pagination={false} dataSource={rows} columns={columns} onRow={(r) => (r._key === 'total' ? { style: { fontWeight: 600 } } : {})} />
        )}
      </Card>
    </Flex>
  );
}

function Revenue({ result }: { result: ReportResult }): React.JSX.Element {
  const { t } = useTranslation('workOrders');
  const noData = t('reports.noData');
  const incomplete = (n: Cell | undefined, total = false): React.ReactNode =>
    num(n) > 0 ? <Tag color="gold">{total ? t('reports.incomplete') : t('reports.incompleteCount', { count: num(n) })}</Tag> : null;
  const byType = [...result.rows.map((r, i) => ({ ...r, _key: String(i) })), { ...result.summary, workType: t('reports.total'), _key: 'total' }];
  const labour = result.sections?.find((x) => x.key === 'labour');
  const labourRows = labour === undefined ? [] : [...labour.rows.map((r, i) => ({ ...r, _key: String(i) })), { name: t('reports.totalLabour'), revenue: labour.summary.revenue ?? 0, share: 1, _key: 'total' }];
  const money = (v: Cell | undefined): string => formatMoney(num(v));
  return (
    <Flex vertical gap={16}>
      <Card size="small" title={t('reports.byType')}>
        {result.rows.length === 0 ? (
          <Empty description={t('reports.empty')} />
        ) : (
          <Table
            size="small"
            rowKey="_key"
            pagination={false}
            dataSource={byType}
            onRow={(r) => (r._key === 'total' ? { style: { fontWeight: 600 } } : {})}
            columns={[
              { title: t('reports.col.workType'), dataIndex: 'workType', key: 'workType' },
              { title: t('reports.col.revenue'), key: 'revenue', align: 'right', render: (_: unknown, r: Record<string, Cell>) => money(r.revenue) },
              { title: t('reports.col.partsCost'), key: 'parts', align: 'right', render: (_: unknown, r: Record<string, Cell>) => money(r.partsCost) },
              { title: t('reports.col.outsourced'), key: 'out', align: 'right', render: (_: unknown, r: Record<string, Cell>) => money(r.outsourced) },
              { title: t('reports.col.margin'), key: 'margin', align: 'right', render: (_: unknown, r: Record<string, Cell>) => money(r.margin) },
              { title: t('reports.col.marginPct'), key: 'pct', align: 'right', render: (_: unknown, r: Record<string, Cell>) => percent(r.marginPct, 1, noData) },
              { title: '', key: 'flag', render: (_: unknown, r: Record<string, Cell>) => incomplete(r.unknownParts, r._key === 'total') },
            ]}
          />
        )}
      </Card>
      <Card size="small" title={t('reports.labour')} extra={<Typography.Text type="secondary">{t('reports.labourNote')}</Typography.Text>}>
        <Table
          size="small"
          rowKey="_key"
          pagination={false}
          dataSource={labourRows}
          onRow={(r) => (r._key === 'total' ? { style: { fontWeight: 600 } } : r.name === null ? { style: { background: WARN_BG } } : {})}
          columns={[
            { title: t('reports.col.performer'), key: 'name', render: (_: unknown, r: Record<string, Cell>) => (r.name === null ? t('reports.notAllocated') : String(r.name)) },
            { title: t('reports.col.labourRevenue'), key: 'revenue', align: 'right', render: (_: unknown, r: Record<string, Cell>) => money(r.revenue) },
            { title: t('reports.col.share'), key: 'share', align: 'right', render: (_: unknown, r: Record<string, Cell>) => percent(r.share, 1, noData) },
          ]}
        />
      </Card>
    </Flex>
  );
}
